import React, { useState } from "react";
import { Play, Square, Circle, Save, FolderOpen, Download, Timer, Undo2, Redo2, LogIn, LogOut, User, Trash2, FilePlus, Share2, Copy, Check, Loader2, Crown, Lock, Shield, Gift, Sparkles, LifeBuoy } from "lucide-react";
import { toast } from "sonner";
import { useStudio } from "@/studio/StudioContext";
import { useAuth } from "@/auth/AuthContext";
import { useSubscription } from "@/subscription/SubscriptionContext";
import { formatApiErrorDetail } from "@/auth/AuthContext";
import AIStarter from "@/studio/AIStarter";
import BeatGenerator from "@/studio/BeatGenerator";
import Songwriter from "@/studio/Songwriter";
import AdminPanel from "@/studio/AdminPanel";
import RedeemDialog from "@/studio/RedeemDialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function TopBar() {
  const {
    isPlaying,
    togglePlay,
    stop,
    bpm,
    setBpm,
    metronome,
    toggleMetronome,
    projectName,
    setProjectName,
    saveProject,
    listProjects,
    deleteProject,
    loadProject,
    newProject,
    shareProject,
    generateCover,
    engine,
    undo,
    redo,
  } = useStudio();
  const { user, logout, requireAuth, setShowAuth } = useAuth();
  const { isPro, openUpgrade, refresh, sub } = useSubscription();
  const [projects, setProjects] = useState([]);
  const [loadOpen, setLoadOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [sharing, setSharing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [coverUrl, setCoverUrl] = useState("");
  const [coverBusy, setCoverBusy] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [redeemOpen, setRedeemOpen] = useState(false);
  const signedIn = !!(user && user.id);

  const handleSave = async () => {
    if (!requireAuth()) return;
    try {
      await saveProject();
      toast.success("Song saved to your account");
      refresh();
    } catch (e) {
      if (e.response?.status === 401) setShowAuth(true);
      else if (e.response?.status === 402)
        openUpgrade(formatApiErrorDetail(e.response?.data?.detail));
      else toast.error("Could not save song");
    }
  };

  const openLoad = async () => {
    if (!requireAuth()) return;
    try {
      const list = await listProjects();
      setProjects(list);
      setLoadOpen(true);
    } catch (e) {
      if (e.response?.status === 401) setShowAuth(true);
      else toast.error("Could not fetch songs");
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    try {
      await deleteProject(id);
      setProjects((prev) => prev.filter((p) => p.id !== id));
      toast.success("Song deleted");
    } catch (err) {
      toast.error("Could not delete song");
    }
  };

  const handleShare = async () => {
    if (!requireAuth()) return;
    setSharing(true);
    setCopied(false);
    setCoverUrl("");
    try {
      const data = await shareProject();
      const url = `${window.location.origin}/s/${data.share_id}`;
      setShareUrl(url);
      if (data.cover_url) setCoverUrl(`${process.env.REACT_APP_BACKEND_URL}${data.cover_url}`);
      setShareOpen(true);
    } catch (e) {
      if (e.response?.status === 401) setShowAuth(true);
      else toast.error("Could not create a share link");
    }
    setSharing(false);
  };

  const handleGenerateCover = async () => {
    setCoverBusy(true);
    toast.info("Generating AI cover art…");
    try {
      const url = await generateCover();
      setCoverUrl(url ? `${process.env.REACT_APP_BACKEND_URL}${url}` : "");
      toast.success("Cover art ready — it now shows on your share link");
    } catch (e) {
      toast.error("Could not generate cover art right now");
    }
    setCoverBusy(false);
  };

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      toast.error("Copy failed — select and copy manually");
    }
  };

  const doExport = async (type) => {
    if (!isPro) {
      openUpgrade("Exporting to WAV is a Pro feature. Start your free trial to download your songs.");
      return;
    }
    setExporting(true);
    toast.info(type === "stems" ? "Rendering stems…" : "Rendering mixdown…");
    try {
      const bars = engine.started ? engine.songBars : 2;
      if (type === "stems") await engine.exportStems(bars);
      else await engine.exportMixdown(bars);
      toast.success("Export complete — check your downloads");
    } catch (e) {
      toast.error("Export failed");
    }
    setExporting(false);
  };

  return (
    <header className="h-14 metal-panel border-b border-[#262626] flex items-center justify-between px-4 shrink-0 relative z-20 max-lg:justify-start max-lg:gap-3 max-lg:overflow-x-auto no-scrollbar">
      <div className="flex items-center gap-4 shrink-0">
        <div className="flex items-center gap-2">
          <img src="/wordmark.png" alt="BeatMXR" className="h-12 md:h-14 object-contain -my-1" />
        </div>

        <div className="flex items-center gap-1 ml-2">
          <button
            data-testid="transport-play-btn"
            onClick={togglePlay}
            className="w-10 h-10 rounded-sm border border-[#404040] flex items-center justify-center hover:bg-[#242424] transition-colors"
            style={isPlaying ? { background: "rgba(0,255,102,0.15)", borderColor: "#00ff66" } : {}}
          >
            {isPlaying ? (
              <Square size={16} className="text-[#00ff66]" fill="#00ff66" />
            ) : (
              <Play size={16} className="text-[#00ff66]" fill="#00ff66" />
            )}
          </button>
          <button
            data-testid="transport-stop-btn"
            onClick={stop}
            className="w-10 h-10 rounded-sm border border-[#404040] flex items-center justify-center hover:bg-[#242424] transition-colors"
          >
            <Square size={15} className="text-neutral-300" />
          </button>
          <button
            data-testid="metronome-btn"
            onClick={toggleMetronome}
            className="w-10 h-10 rounded-sm border border-[#404040] flex items-center justify-center hover:bg-[#242424] transition-colors"
            style={metronome ? { background: "rgba(0,229,255,0.15)", borderColor: "#00e5ff" } : {}}
          >
            <Timer size={16} className={metronome ? "text-[#00e5ff]" : "text-neutral-400"} />
          </button>
          <div className="w-px h-6 bg-[#262626] mx-1" />
          <button
            data-testid="undo-btn"
            onClick={undo}
            title="Undo"
            className="w-10 h-10 rounded-sm border border-[#404040] flex items-center justify-center hover:bg-[#242424] transition-colors"
          >
            <Undo2 size={15} className="text-neutral-300" />
          </button>
          <button
            data-testid="redo-btn"
            onClick={redo}
            title="Redo"
            className="w-10 h-10 rounded-sm border border-[#404040] flex items-center justify-center hover:bg-[#242424] transition-colors"
          >
            <Redo2 size={15} className="text-neutral-300" />
          </button>
        </div>

        <div className="flex items-center gap-2 ml-2 px-3 py-1 rounded-sm bg-[#050505] border border-[#262626]">
          <span className="hw-label">BPM</span>
          <input
            data-testid="bpm-input"
            type="number"
            min={40}
            max={240}
            value={bpm}
            onChange={(e) => setBpm(Number(e.target.value))}
            className="w-14 bg-transparent font-mono-data text-[#00e5ff] text-lg outline-none"
          />
        </div>

        <AIStarter />
        <BeatGenerator />
        <Songwriter />
      </div>

      <div className="flex items-center gap-2 shrink-0">
        <input
          data-testid="project-name-input"
          value={projectName}
          onChange={(e) => setProjectName(e.target.value)}
          className="bg-[#050505] border border-[#262626] rounded-sm px-3 py-1.5 text-sm font-mono-data text-neutral-300 w-48 outline-none focus:border-cyan-500 transition-colors hidden md:block"
        />
        <button
          data-testid="save-project-btn"
          onClick={handleSave}
          className="flex items-center gap-1.5 px-3 py-2 rounded-sm border border-[#404040] hover:bg-[#242424] transition-colors text-xs font-semibold"
        >
          <Save size={14} /> Save
        </button>
        <button
          data-testid="open-load-btn"
          onClick={openLoad}
          className="flex items-center gap-1.5 px-3 py-2 rounded-sm border border-[#404040] hover:bg-[#242424] transition-colors text-xs font-semibold"
        >
          <FolderOpen size={14} /> Load
        </button>
        <button
          data-testid="share-song-btn"
          onClick={handleShare}
          disabled={sharing}
          className="flex items-center gap-1.5 px-3 py-2 rounded-sm border border-[#404040] hover:bg-[#242424] transition-colors text-xs font-semibold disabled:opacity-50"
        >
          {sharing ? <Loader2 size={14} className="animate-spin" /> : <Share2 size={14} />} Share
        </button>

        {isPro ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="export-menu-btn"
                disabled={exporting}
                className="flex items-center gap-1.5 px-3 py-2 rounded-sm bg-cyan-500 text-black hover:bg-cyan-400 transition-colors text-xs font-bold disabled:opacity-50"
              >
                <Download size={14} /> {exporting ? "Rendering…" : "Export"}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-[#141414] border-[#262626] text-neutral-200">
              <DropdownMenuItem data-testid="export-wav-item" onClick={() => doExport("mix")}>
                Mixdown → WAV
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="export-stems-item" onClick={() => doExport("stems")}>
                Individual Stems → WAV
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <button
            data-testid="export-menu-btn"
            onClick={() => doExport("mix")}
            className="flex items-center gap-1.5 px-3 py-2 rounded-sm bg-cyan-500 text-black hover:bg-cyan-400 transition-colors text-xs font-bold"
          >
            <Lock size={13} /> Export
          </button>
        )}

        {!isPro && (
          <button
            data-testid="upgrade-pro-btn"
            onClick={() => openUpgrade()}
            className="flex items-center gap-1.5 px-3 py-2 rounded-sm text-xs font-bold text-black transition-transform hover:scale-[1.03]"
            style={{ background: "linear-gradient(120deg,#ffd166,#ff007f)", boxShadow: "0 0 14px rgba(255,0,127,0.4)" }}
          >
            <Crown size={14} /> Go Pro
          </button>
        )}
        {isPro && (
          <span
            data-testid="pro-badge"
            className="flex items-center gap-1 px-2.5 py-2 rounded-sm text-[10px] font-bold text-black font-mono-data"
            style={{ background: "linear-gradient(120deg,#ffd166,#00e5ff)" }}
          >
            <Crown size={12} /> PRO
          </span>
        )}

        <div className="w-px h-6 bg-[#262626] mx-1" />
        {signedIn ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="account-menu-btn"
                className="flex items-center gap-1.5 px-3 py-2 rounded-sm border border-[#404040] hover:bg-[#242424] transition-colors text-xs font-semibold max-w-[140px]"
              >
                <User size={14} className="text-[#00e5ff] shrink-0" />
                <span className="truncate">{user.name}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-[#141414] border-[#262626] text-neutral-200">
              {sub && (
                <div className="px-2 py-2 border-b border-[#262626] mb-1" data-testid="usage-meter">
                  <div className="flex items-center justify-between gap-3 mb-1.5">
                    <span className="hw-label text-[9px]">Plan</span>
                    <span
                      className="font-mono-data text-[10px] font-bold"
                      style={{ color: isPro ? "#00e5ff" : "#a3a3a3" }}
                    >
                      {isPro ? "PRO ✦" : "FREE"}
                    </span>
                  </div>
                  {!isPro && (
                    <div className="flex flex-col gap-1 mt-1">
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-[10px] text-neutral-400">Saved songs</span>
                        <span className="font-mono-data text-[10px] text-neutral-200" data-testid="usage-songs">
                          {sub.usage.songs}/{sub.limits.max_songs}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-[10px] text-neutral-400">AI starts today</span>
                        <span className="font-mono-data text-[10px] text-neutral-200" data-testid="usage-ai">
                          {sub.usage.ai_today}/{sub.limits.ai_per_day}
                        </span>
                      </div>
                      <button
                        data-testid="menu-upgrade-btn"
                        onClick={() => openUpgrade()}
                        className="mt-1.5 w-full flex items-center justify-center gap-1 py-1.5 rounded-sm text-[10px] font-bold text-black"
                        style={{ background: "linear-gradient(120deg,#ffd166,#ff007f)" }}
                      >
                        <Crown size={11} /> Upgrade to Pro
                      </button>
                    </div>
                  )}
                </div>
              )}
              <DropdownMenuItem
                data-testid="new-song-item"
                onClick={() => {
                  newProject();
                  toast.info("Started a new song");
                }}
              >
                <FilePlus size={14} className="mr-2" /> New Song
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="redeem-code-item"
                onClick={() => setRedeemOpen(true)}
              >
                <Gift size={14} className="mr-2" /> Redeem Code
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="contact-support-item"
                onClick={() => window.open("/support", "_blank")}
              >
                <LifeBuoy size={14} className="mr-2 text-[#ff007f]" /> Contact Support
              </DropdownMenuItem>
              {user.role === "admin" && (
                <DropdownMenuItem
                  data-testid="admin-panel-item"
                  onClick={() => setAdminOpen(true)}
                >
                  <Shield size={14} className="mr-2 text-[#00e5ff]" /> Admin — Give Pro
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                data-testid="logout-item"
                onClick={() => {
                  logout();
                  toast.success("Signed out");
                }}
              >
                <LogOut size={14} className="mr-2" /> Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <button
            data-testid="signin-btn"
            onClick={() => setShowAuth(true)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-sm border border-[#00e5ff] text-[#00e5ff] hover:bg-[#00e5ff]/10 transition-colors text-xs font-bold"
          >
            <LogIn size={14} /> Sign In
          </button>
        )}
      </div>

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="bg-[#0f0f0f] border-[#262626] text-white max-w-md" data-testid="share-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-widest flex items-center gap-2">
              <Share2 size={16} className="text-[#00e5ff]" /> SHARE YOUR SONG
            </DialogTitle>
            <DialogDescription className="sr-only">Copy your public song link to share it anywhere.</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-neutral-400 -mt-1">
            Anyone with this link can listen to "{projectName}" right in their browser — no app needed.
          </p>
          <div className="flex items-center gap-2 mt-2">
            <input
              data-testid="share-url-input"
              readOnly
              value={shareUrl}
              onFocus={(e) => e.target.select()}
              className="flex-1 bg-[#050505] border border-[#262626] rounded-sm px-3 py-2 text-xs font-mono-data text-[#00e5ff] outline-none"
            />
            <button
              data-testid="share-copy-btn"
              onClick={copyShare}
              className="flex items-center gap-1.5 px-3 py-2 rounded-sm bg-cyan-500 text-black font-bold text-xs hover:bg-cyan-400 transition-colors"
            >
              {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copied" : "Copy"}
            </button>
          </div>
          <p className="text-[11px] text-neutral-600 mt-1">
            Tip: paste it in your Instagram bio, a TikTok caption, or send it to friends.
          </p>

          <div className="mt-3 pt-3 border-t border-[#262626]">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="hw-label text-[10px] flex items-center gap-1.5">
                  <Sparkles size={12} className="text-[#ff007f]" /> AI cover art
                </div>
                <p className="text-[11px] text-neutral-500 mt-0.5">A shareable cover for social posts.</p>
              </div>
              <button
                data-testid="share-cover-btn"
                onClick={handleGenerateCover}
                disabled={coverBusy}
                className="flex items-center gap-1.5 px-3 py-2 rounded-sm text-[11px] font-bold text-black disabled:opacity-60"
                style={{ background: "linear-gradient(120deg,#ffd166,#ff007f)" }}
              >
                {coverBusy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                {coverUrl ? "Regenerate" : "Generate cover"}
              </button>
            </div>
            {coverUrl && (
              <img
                data-testid="share-cover-preview"
                src={coverUrl}
                alt="AI cover art"
                className="mt-3 w-40 h-40 object-cover rounded-md border border-[#262626]"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={loadOpen} onOpenChange={setLoadOpen}>
        <DialogContent className="bg-[#141414] border-[#262626] text-white">
          <DialogHeader>
            <DialogTitle className="font-display tracking-widest">LOAD SESSION</DialogTitle>
          </DialogHeader>
          <div className="max-h-80 overflow-y-auto flex flex-col gap-2 mt-2">
            {projects.length === 0 && (
              <p className="text-neutral-500 text-sm">No saved sessions yet.</p>
            )}
            {projects.map((p) => (
              <div
                key={p.id}
                className="flex items-center gap-2 group"
              >
                <button
                  data-testid={`load-project-${p.id}`}
                  onClick={() => {
                    loadProject(p);
                    setLoadOpen(false);
                    toast.success(`Loaded "${p.name}"`);
                  }}
                  className="flex-1 text-left px-3 py-2 rounded-sm border border-[#262626] hover:border-cyan-500 hover:bg-[#1e1e1e] transition-colors"
                >
                  <div className="font-semibold text-sm">{p.name}</div>
                  <div className="font-mono-data text-[10px] text-neutral-500">
                    {p.data?.bpm} BPM · {new Date(p.updated_at || p.created_at).toLocaleString()}
                  </div>
                </button>
                <button
                  data-testid={`delete-project-${p.id}`}
                  onClick={(e) => handleDelete(p.id, e)}
                  title="Delete song"
                  className="w-9 h-9 shrink-0 rounded-sm border border-[#262626] flex items-center justify-center text-neutral-500 hover:text-[#ff3333] hover:border-[#ff3333] transition-colors"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      <AdminPanel open={adminOpen} onOpenChange={setAdminOpen} />
      <RedeemDialog open={redeemOpen} onOpenChange={setRedeemOpen} />
    </header>
  );
}
